import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getChosenBusiness } from 'store/authentication/selectors';
import { KizenTypography } from '__app/typography';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import TextInput from '__components/Inputs/TextInput';
import { BUSINESS_APP_ACTIONS } from 'ts-components/Plugins/businessApps/constants';
import type { PendingBusinessAppAction } from 'ts-components/Plugins/businessApps/useBusinessAppActions';
import { useBusinessAppPreviewInstalls } from 'ts-components/Plugins/businessApps/useBusinessAppPreviewInstalls';
import type { BusinessAppPreviewInstalls } from 'ts-components/Plugins/businessApps/types';
import { liveVersionOf } from 'ts-components/Plugins/businessApps/helpers';

const PreviewImpact: FC<{
  data: BusinessAppPreviewInstalls;
  usersMessage: string;
}> = ({ data, usersMessage }) => {
  const { t } = useTranslation();
  const others = data.installed_by.filter(
    (install) => !install.is_owner && install.is_active
  );

  return (
    <>
      {others.length ? (
        <>
          <KizenTypography className="leading-[1.25]">{usersMessage}</KizenTypography>
          <ul className="m-0 pl-[18px]">
            {others.map((install) => (
              <li key={install.user.id}>
                <KizenTypography className="leading-[1.25]">{install.user.full_name}</KizenTypography>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {data.pinned_steps_count ? (
        <KizenTypography className="leading-[1.25]" color="var(--color-feedback-error)">
          {data.pinned_steps_count === 1
            ? t(
                '1 automation step uses this preview and will fail until it is deleted and added again.'
              )
            : t(
                '{{count}} automation steps use this preview and will fail until they are deleted and added again.',
                { count: data.pinned_steps_count }
              )}
        </KizenTypography>
      ) : null}
    </>
  );
};

type ConfirmActionModalProps = {
  pending: PendingBusinessAppAction;
  onConfirm: () => Promise<void>;
  onHide: () => void;
};

export const ConfirmActionModal: FC<ConfirmActionModalProps> = ({
  pending,
  onConfirm,
  onHide,
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [typedName, setTypedName] = useState('');
  const business = useSelector(getChosenBusiness);
  const { action, app, version } = pending;
  const previewOwnerId = version?.preview_owner?.id;
  const { data: installs, isLoading: installsLoading } =
    useBusinessAppPreviewInstalls(app.api_name, previewOwnerId);
  const disabled = submitting || installsLoading;

  const handleConfirm = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }, [submitting, onConfirm]);

  // Backend deletes the app with its last version
  const lastVersionNotice =
    app.versions.length === 1 ? (
      <KizenTypography className="leading-[1.25]" color="var(--color-feedback-error)">
        {t(
          'This is the only version of {{name}}. The app, its settings and its secrets will be deleted.',
          { name: app.name }
        )}
      </KizenTypography>
    ) : null;

  const liveVersion = liveVersionOf(app)?.version;
  const activeUsers = version?.active_users_count ?? 0;
  const activeUsersNotice = (
    <KizenTypography className="leading-[1.25]">
      {activeUsers === 1
        ? t('1 person is using published version {{version}} right now.', {
            version: version?.version,
          })
        : activeUsers
          ? t(
              '{{count}} people are using published version {{version}} right now.',
              {
                count: activeUsers,
                version: version?.version,
              }
            )
          : t('Nobody is using published version {{version}} right now.', {
              version: version?.version,
            })}
    </KizenTypography>
  );

  const nameMatches =
    typedName.trim().toLowerCase() === app.name.trim().toLowerCase();

  const previewImpact = (usersMessage: string) =>
    installs ? (
      <PreviewImpact data={installs} usersMessage={usersMessage} />
    ) : null;

  if (action === BUSINESS_APP_ACTIONS.deletePreview) {
    return (
      <ConfirmDeletionModal
        show
        disabled={disabled}
        onConfirm={handleConfirm}
        onHide={onHide}
        data-qa-id="business-app-delete-preview-modal"
      >
        <div className="flex flex-col gap-[10px] !text-start">
          <KizenTypography className="leading-[1.25]">
            {t(
              'This will delete this preview of {{name}}. Other versions and Kizen records are not affected.',
              { name: app.name }
            )}
          </KizenTypography>
          {previewImpact(
            liveVersion
              ? t(
                  'These users are using this preview and will move to their own preview, or to published version {{version}}:',
                  { version: liveVersion }
                )
              : t(
                  'These users are using this preview and will move to their own preview, or lose access to {{name}}:',
                  { name: app.name }
                )
          )}
          {lastVersionNotice}
        </div>
      </ConfirmDeletionModal>
    );
  }

  if (action === BUSINESS_APP_ACTIONS.deleteApp) {
    return (
      <ConfirmDeletionModal
        show
        size="medium"
        disabled={disabled || !nameMatches}
        onConfirm={handleConfirm}
        onHide={onHide}
        data-qa-id="business-app-delete-app-modal"
      >
        <div className="flex flex-col gap-[10px] !text-start">
          <KizenTypography className="leading-[1.25]">
            {t(
              "Are you sure you want to delete {{name}}? It's removed for everyone in {{business}} with all its versions and previews, and its settings and secrets are deleted. Records are kept. Please type “{{name}}” below to confirm.",
              { name: app.name, business: business?.name }
            )}
          </KizenTypography>
          <TextInput
            value={typedName}
            onChange={setTypedName}
            placeholder={t('Type {{name}} to confirm', { name: app.name })}
            inModal
            data-qa-id="business-app-delete-confirm-name-input"
          />
        </div>
      </ConfirmDeletionModal>
    );
  }

  if (action === BUSINESS_APP_ACTIONS.deleteVersion) {
    return (
      <ConfirmDeletionModal
        show
        disabled={disabled}
        onConfirm={handleConfirm}
        onHide={onHide}
        data-qa-id="business-app-delete-version-modal"
      >
        <div className="flex flex-col gap-[10px] !text-start">
          <KizenTypography className="leading-[1.25]">
            {t(
              'Are you sure you want to delete version {{version}} of {{name}}? Records are kept.',
              { version: version?.version, name: app.name }
            )}
          </KizenTypography>
          {lastVersionNotice}
        </div>
      </ConfirmDeletionModal>
    );
  }

  if (action === BUSINESS_APP_ACTIONS.disable) {
    return (
      <ConfirmationModal
        show
        heading={t('Confirm Disable')}
        buttonText={t('Disable')}
        defaultLeftBtnText={t('Cancel')}
        disabled={disabled}
        onConfirm={handleConfirm}
        onHide={onHide}
        data-qa-id="business-app-disable-modal"
      >
        <div className="flex flex-col gap-[10px] !text-start">
          <KizenTypography className="leading-[1.25]">
            {t(
              'Published version {{version}} of {{name}} will stop for everyone. Previews keep running for anyone using them, and settings and secrets are kept.',
              { version: version?.version, name: app.name }
            )}
          </KizenTypography>
          {activeUsersNotice}
        </div>
      </ConfirmationModal>
    );
  }

  if (action === BUSINESS_APP_ACTIONS.publishPreview) {
    return (
      <ConfirmationModal
        show
        heading={t('Confirm Publish')}
        buttonText={t('Publish')}
        defaultLeftBtnText={t('Cancel')}
        disabled={disabled}
        onConfirm={handleConfirm}
        onHide={onHide}
        data-qa-id="business-app-publish-modal"
      >
        <div className="flex flex-col gap-[10px] !text-start">
          <KizenTypography className="leading-[1.25]">
            {t(
              'This preview of {{name}} becomes the next published version for everyone in this business. The preview is removed, and anyone using it moves to the new published version.',
              { name: app.name }
            )}
          </KizenTypography>
          {previewImpact(
            t(
              'These users are using this preview and will move to the published version:'
            )
          )}
        </div>
      </ConfirmationModal>
    );
  }

  return null;
};
